import React, { useState, useContext } from "react";
import "./Header.css";
import { LanguageContext } from "../locales/LanguageContext";
import headerTranslations from "../locales/headerTranslations";

import { useNavigate, Link, NavLink } from "react-router-dom";

import { handleLogout as commonLogout } from "../utils/logout";
import { decodeJwt } from "../utils/jwtDecode";

const Header = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token"),
  );
  const [isAdmin, setIsAdmin] = useState(false);

  // Listen for login/logout changes in other tabs and in this tab
  React.useEffect(() => {
    const syncAuth = () => {
      const token = localStorage.getItem("token");
      setIsAuthenticated(!!token);
      if (token) {
        const payload = decodeJwt(token);
        setIsAdmin(payload?.roleId?.name === "Admin");
      } else {
        setIsAdmin(false);
      }
    };
    window.addEventListener("storage", syncAuth);
    // Also patch window.localStorage.setItem and removeItem to trigger state update in this tab
    const originalSetItem = window.localStorage.setItem;
    const originalRemoveItem = window.localStorage.removeItem;
    window.localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, arguments);
      if (key === "token") syncAuth();
    };
    window.localStorage.removeItem = function (key) {
      originalRemoveItem.apply(this, arguments);
      if (key === "token") syncAuth();
    };
    // Initial check
    syncAuth();
    return () => {
      window.removeEventListener("storage", syncAuth);
      window.localStorage.setItem = originalSetItem;
      window.localStorage.removeItem = originalRemoveItem;
    };
  }, []);
  const [navCollapsed, setNavCollapsed] = useState(true);
  const navigate = useNavigate();

  // Use common logout handler
  function handleLogout() {
    commonLogout({ setIsAuthenticated, navigate });
  }

  const handleNavCollapse = () => setNavCollapsed(!navCollapsed);

  const { lang, setLang } = useContext(LanguageContext);
  const t = headerTranslations[lang] || {};

  return (
    <div className="header_section">
      <div className="container-fluid">
        <nav
          className="navbar navbar-expand-lg navbar-light bg-light position-relative"
          style={{ zIndex: 1050 }}
        >
          <Link
            className="navbar-brand"
            to="/"
            onClick={() => setNavCollapsed(true)}
            tabIndex={0}
          >
            <img
              src="/assets/images/logo.png"
              alt="AKAAL KIDS Logo"
              style={{ maxHeight: 48 }}
            />
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            aria-controls="navbarSupportedContent"
            aria-expanded={!navCollapsed}
            aria-label="Toggle navigation"
            onClick={handleNavCollapse}
            style={{
              outline: "none",
              border: "none",
              padding: 10,
              borderRadius: 6,
            }}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          {/* Overlay for mobile menu */}
          {!navCollapsed && (
            <div
              className="header-backdrop"
              onClick={handleNavCollapse}
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.3)",
                zIndex: 1049,
                transition: "background 0.3s",
              }}
            />
          )}
          <div
            className={
              "collapse navbar-collapse" +
              (navCollapsed ? "" : " show responsive-menu-open")
            }
            id="navbarSupportedContent"
            style={{
              zIndex: 1100,
              background: navCollapsed ? undefined : "#fff",
              transition: "all 0.3s",
              boxShadow: !navCollapsed ? "0 8px 32px #0002" : undefined,
              borderRadius: !navCollapsed ? 12 : undefined,
              minHeight: 0,
            }}
            role="menu"
          >
            <ul
              className="navbar-nav mr-auto"
              style={{ width: "100%", gap: 4 }}
            >
              <li className="nav-item">
                <NavLink
                  className={({ isActive }) =>
                    "nav-link" +
                    (isActive && window.location.pathname === "/"
                      ? " active"
                      : "")
                  }
                  to="/"
                  end
                  onClick={() => setNavCollapsed(true)}
                >
                  {t.home}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  className={({ isActive }) =>
                    "nav-link" + (isActive ? " active" : "")
                  }
                  to="/about"
                  onClick={() => setNavCollapsed(true)}
                >
                  {t.about}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  className={({ isActive }) =>
                    "nav-link" + (isActive ? " active" : "")
                  }
                  to="/batches"
                  onClick={() => setNavCollapsed(true)}
                >
                  {t.batches}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  className={({ isActive }) =>
                    "nav-link" + (isActive ? " active" : "")
                  }
                  to="/procedure"
                  onClick={() => setNavCollapsed(true)}
                >
                  {t.procedure}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  className={({ isActive }) =>
                    "nav-link" + (isActive ? " active" : "")
                  }
                  to="/help"
                  onClick={() => setNavCollapsed(true)}
                >
                  {t.help}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  className={({ isActive }) =>
                    "nav-link" + (isActive ? " active" : "")
                  }
                  to="/contact"
                  onClick={() => setNavCollapsed(true)}
                >
                  {t.contact}
                </NavLink>
              </li>{" "}
              {isAuthenticated && isAdmin && (
                <li className="nav-item">
                  <NavLink
                    className={({ isActive }) =>
                      "nav-link" + (isActive ? " active" : "")
                    }
                    to="/admin/dashboard"
                    onClick={() => setNavCollapsed(true)}
                  >
                    {t.admin}
                  </NavLink>
                </li>
              )}
            </ul>
            <div>
              {/* User Icons */}
              {!isAuthenticated && (
                <div className="user_icon">
                  <Link to="/login" onClick={() => setNavCollapsed(true)}>
                    <i className="fa fa-user" aria-hidden="true"></i>
                  </Link>
                </div>
              )}
              {isAuthenticated && (
                <>
                  <div className="user_icon">
                    <Link to="/profile" onClick={() => setNavCollapsed(true)}>
                      <i className="fa fa-user" aria-hidden="true"></i>
                    </Link>
                  </div>
                  <div className="user_icon">
                    <a
                      href="#logout"
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogout();
                        setNavCollapsed(true);
                      }}
                    >
                      <i className="fa fa-sign-out" aria-hidden="true"></i>
                    </a>
                  </div>
                </>
              )}
              {/* Language Switcher */}
              <div>
                <select
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  style={{
                    borderRadius: 6,
                    padding: "6px 12px",
                    border: "1px solid #ccc",
                    fontWeight: 600,
                    minWidth: 90,
                    fontSize: 16,
                  }}
                  aria-label={t.languageLabel}
                >
                  <option value="en">English</option>
                  <option value="pa">ਪੰਜਾਬੀ</option>
                </select>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};
export default Header;
